import React, { useMemo, useState } from "react";

import { Grid } from "@mui/material";
import { Skeleton, Stack } from "@mui/material";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { MdOutlineAddCircleOutline } from "react-icons/md";

import { useAppState } from "../../../../context/StateContext";
import useActionShipment from "../../../../hooks/useActionShipment";
import { Document, DocumentReference } from "../../../../models/document";
import { FileInput, ShipmentDocument } from "../../../../models/shipment.document";
import { Shipment } from "../../../../models/shipments";
import { ValueOf } from "../../../../types";
import Button, { Color } from "../../../Form/Button";
import SkeletonComponent from "../../../Generic/Skeleton";
import DocumentsItem from "./DocumentsItem";
import ProgressBar from "./ProgressBar";
import useStyles from "./styles";
import { mountShipmentDocumentFullData } from "./types";
import UploadDocumentModal, { FileData } from "./UploadDocumentModal";

interface ShipmentDocumentsProps {
  companyDocuments: Document[];
  loadingResources: boolean;
  openedFromView?: boolean;
  viewOnly: boolean;
  shipmentDocuments?: ShipmentDocument[];
  onUpdateShipment?: (key: keyof Shipment, value: ValueOf<Shipment> | null) => void;
  loadingShipment?: boolean;
  onDownloadDocument?: (
    documentId: number,
    documentType?: DocumentReference,
    onFinally?: () => void
  ) => void;
  onDownloadDocumentCsv?: (
    documentId: number,
    documentType?: DocumentReference,
    onFinally?: () => void
  ) => void;
  loadingAllDocuments?: boolean;
  onDownloadAllDocuments?: () => void;
  loadingUploadCsvDocument?: boolean;
  isBooking?: boolean;
}

const ShipmentDocuments = ({
  companyDocuments,
  loadingResources,
  viewOnly,
  openedFromView,
  shipmentDocuments,
  onUpdateShipment,
  loadingShipment,
  onDownloadDocument,
  onDownloadDocumentCsv,
  loadingAllDocuments = false,
  onDownloadAllDocuments,
  loadingUploadCsvDocument,
  isBooking,
}: ShipmentDocumentsProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { useUploadDocument, useUploadMultipleDocuments, useClearDocument, useRemoveArchive } =
    useActionShipment();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [documentIdsUploading, setDocumentIdsUploading] = useState<Array<number | undefined>>([]);
  const [documentIdsClearing, setDocumentIdsClearing] = useState<Array<number | undefined>>([]);

  const onAddNewDocumentInShipment = (shipmentDocumentCreated: ShipmentDocument) => {
    const shipmentDocumentList = _.cloneDeep(shipmentDocuments || []);
    const shipmentIndex = shipmentDocumentList.findIndex(
      (shipmentDocument) => shipmentDocument.id === shipmentDocumentCreated.id
    );

    if (shipmentIndex > -1) {
      shipmentDocumentList[shipmentIndex] = shipmentDocumentCreated;
    } else {
      shipmentDocumentList.push(shipmentDocumentCreated);
    }

    if (onUpdateShipment) onUpdateShipment("documents", shipmentDocumentList);
    setDocumentIdsUploading((prev) =>
      prev.filter((id) => id !== shipmentDocumentCreated?.documentId)
    );
  };

  const onClearDocumentInShipment = (shipmentDocumentCleared: ShipmentDocument) => {
    const shipmentDocumentList = _.cloneDeep(shipmentDocuments || []);
    const documentIndex = shipmentDocumentList.findIndex(
      (shipmentDocument) => shipmentDocument.id === shipmentDocumentCleared.id
    );

    if (documentIndex > -1) {
      shipmentDocumentList[documentIndex] = shipmentDocumentCleared;
      onUpdateShipment?.("documents", shipmentDocumentList);
    }

    setDocumentIdsClearing((prev) =>
      prev.filter((id) => id !== shipmentDocumentCleared?.documentId)
    );
  };

  const { mutate: uploadDocument, isLoading: loadingUpload } = useUploadDocument(
    onAddNewDocumentInShipment
  );

  const { mutate: uploadMultipleDocuments, isLoading: loadingUploadMultiple } =
    useUploadMultipleDocuments(onAddNewDocumentInShipment);

  const { mutate: clearDocument } = useClearDocument(onClearDocumentInShipment);

  const onArchiveRemoved = (documentId: number, archiveId: number) => {
    const shipmentDocumentList = _.cloneDeep(shipmentDocuments || []);
    const documentIndex = shipmentDocumentList.findIndex((doc) => doc.id === documentId);

    if (documentIndex === -1) return;

    const target = shipmentDocumentList[documentIndex];
    const remaining = (target.archives ?? []).filter((archive) => archive.id !== archiveId);

    shipmentDocumentList[documentIndex] = {
      ...target,
      archives: remaining,
      ...(remaining.length === 0
        ? { uploaded: false, file: null, filename: null, fileType: null, uploadedAt: null }
        : {}),
    };

    onUpdateShipment?.("documents", shipmentDocumentList);
  };

  const { mutate: removeArchive, isLoading: loadingRemoveArchive } =
    useRemoveArchive(onArchiveRemoved);

  const uploadFileButton = () => (
    <Button
      startIcon={<MdOutlineAddCircleOutline />}
      customColor={Color.PRIMARY}
      className={classes.button}
      onClick={() => setOpenModal(true)}
      disabled={viewOnly}
    >
      {translate("newDocument")}
    </Button>
  );

  const groupedDocuments = useMemo(() => {
    const documentList: ShipmentDocument[] = companyDocuments.map((companyDocument) => {
      return {
        ...companyDocument,
        ...(!companyDocument.generated ? { id: undefined } : {}),
        name: companyDocument.name,
        uploaded: false,
        documentId: companyDocument.id,
      } as ShipmentDocument;
    });

    shipmentDocuments?.forEach((shipmentDocument) => {
      const documentFoundIndex = documentList.findIndex(
        (document) =>
          document.documentId &&
          shipmentDocument.documentId &&
          document.documentId === shipmentDocument.documentId
      );

      if (documentFoundIndex > -1) {
        const found = documentList[documentFoundIndex];

        const documentToList = mountShipmentDocumentFullData(
          shipmentDocument,
          found as unknown as Document
        );

        documentList.splice(documentFoundIndex, 1, documentToList);
      } else {
        documentList.push(shipmentDocument);
      }
    });

    return documentList;
  }, [companyDocuments, shipmentDocuments]);

  const progress = useMemo(() => {
    let count = 0;
    let progress = 0;
    const requiredDocuments = companyDocuments.filter((document) => document.required);
    const divisor = requiredDocuments.length;

    groupedDocuments.forEach((groupedDocument) => {
      const find = requiredDocuments.find(
        (requiredDocument) =>
          requiredDocument?.id === groupedDocument?.documentId && groupedDocument?.uploaded
      );

      if (find) {
        count += 1;
      }
    });

    if (divisor === 0) {
      progress = 100;
    } else if (count > 0 && count <= divisor) {
      progress = (count * 100) / divisor;
    }

    return progress;
  }, [groupedDocuments]);

  const downloadAllDocumentsButton = () => (
    <Button
      variant="outlined"
      customColor={Color.SECONDARY_OUTLINED}
      className={classes.button}
      onClick={() => onDownloadAllDocuments && onDownloadAllDocuments()}
      disabled={!onDownloadAllDocuments || (!progress && !onDownloadDocument)}
      loading={loadingAllDocuments}
    >
      {translate("downloadAllFiles")}
    </Button>
  );

  return (
    <SkeletonComponent isLoading={loadingShipment || loadingResources}>
      <div data-cy="shipment-documents-box" className={classes.mainDiv}>
        <div className={classes.title}>
          {translate(`documentsForThis${isBooking ? "Booking" : "Shipment"}`)}
        </div>
        <Stack direction={"row"} spacing={2} alignItems={"flex-end"}>
          <ProgressBar value={progress} />
          {downloadAllDocumentsButton()}
          {!viewOnly ? uploadFileButton() : null}
        </Stack>

        <Grid container direction="row" spacing={2} className={classes.documentsField}>
          {groupedDocuments.map((groupedDocument, key: number) => {
            const { isFromBooking } = groupedDocument;
            const viewOnlyDocument =
              viewOnly || (isFromBooking && !isBooking) || (!isFromBooking && isBooking);

            return (
              <DocumentsItem
                isBooking={isBooking}
                key={key}
                shipmentDocument={groupedDocument}
                openedFromView={openedFromView}
                viewOnly={viewOnlyDocument}
                onUploadDocument={(uri: string) => {
                  setDocumentIdsUploading((prev) => [...prev, groupedDocument?.documentId]);
                  const shipmentDocumentToSave = {
                    id: groupedDocument.id,
                    file: uri,
                    uploadedAt: new Date().toISOString(),
                    userId: currentUser?.id,
                    documentId: groupedDocument?.documentId,
                    name: groupedDocument?.name,
                    fileType: "pdf",
                    isFromBooking: !!isBooking,
                  } as ShipmentDocument;

                  uploadDocument(shipmentDocumentToSave);
                }}
                onClearDocument={async (shipmentDocumentToClear: ShipmentDocument) => {
                  setDocumentIdsClearing((prev) => [...prev, groupedDocument?.documentId]);

                  if (shipmentDocumentToClear.id) {
                    await clearDocument(shipmentDocumentToClear);
                  }
                }}
                onDeleteDocument={(id: number) => {
                  onUpdateShipment?.(
                    "documents",
                    shipmentDocuments?.filter((doc) => !(doc.id === id))
                  );
                }}
                onRemoveArchive={(archiveId: number) => {
                  if (groupedDocument.id) {
                    removeArchive({ documentId: groupedDocument.id, archiveId });
                  }
                }}
                loadingRemoveArchive={loadingRemoveArchive}
                loadingUpload={
                  !!documentIdsUploading?.find((id) => id === groupedDocument?.documentId)
                }
                loadingClearDocument={
                  !!documentIdsClearing?.find((id) => id === groupedDocument?.documentId)
                }
                disabledUploadButton={documentIdsUploading?.length > 0 || viewOnly}
                onDownloadDocument={onDownloadDocument}
                onDownloadDocumentCsv={onDownloadDocumentCsv}
              />
            );
          })}
          {loadingUploadCsvDocument && (
            <Grid item sm={12} md={6}>
              <Skeleton width="100%" height="100%" className={classes.loading} />
            </Grid>
          )}
        </Grid>

        <UploadDocumentModal
          open={openModal}
          isLoading={false}
          onCloseModal={() => setOpenModal(false)}
          onUploadDocument={(url, name, callbackFunction) => {
            const shipmentDocumentToSave = {
              file: url,
              uploadedAt: new Date().toISOString(),
              userId: currentUser?.id,
              name,
              fileType: "pdf",
            } as ShipmentDocument;

            uploadDocument(shipmentDocumentToSave, {
              onSuccess: () => {
                if (callbackFunction) callbackFunction();
              },
            });
          }}
          onUploadMultipleDocuments={(filesData: FileData[], name, callbackFunction) => {
            const files: FileInput[] = filesData.map((fileData) => ({
              file: fileData.uri,
              filename: fileData.fileName,
              fileType: fileData.fileType,
            }));

            const payload = {
              uploadedAt: new Date().toISOString(),
              userId: currentUser?.id,
              name,
              fileType: "pdf",
              files,
            } as ShipmentDocument & { files: FileInput[] };

            uploadMultipleDocuments(payload, {
              onSuccess: () => {
                if (callbackFunction) callbackFunction();
              },
            });
          }}
          loadingUpload={loadingUpload || loadingUploadMultiple}
        />
      </div>
    </SkeletonComponent>
  );
};

export default ShipmentDocuments;
